/**
 * "Needs attention now" cards (#2485). A pure function of today's numbers:
 * today's board row picks the phase (`phaseOf`, the current step), each phase lists its
 * slots in order (`SLOTS`), a slot yields a card only when its condition holds,
 * and the first four cards win; a live exception card always keeps a place. No slot is padded and no number is made up: a
 * slot whose data has not arrived yields nothing (preflight excepted — it has
 * the board count). Every clock time comes from the tenant flow or the Onfleet
 * ETA; without the flow, a slot that needs a rule time yields nothing.
 *
 * All fetching lives in `useFocusCards`; this module reads only its arguments.
 */
import type { BoardDay, BoardStage, BoardTarget } from './boardModel';
import { dispatchCutoffAhead, hasDispatchWindow } from './flowCopy';
import {
  earliestStraggler,
  type FlowGateCategory,
  type FlowStepModel,
  type Straggler,
} from './flowSteps';
import { phaseOf, type Phase } from './rowCopy';

export type FocusTone = 'live' | 'block' | 'ok' | 'attn' | 'mut';

export type FocusKind =
  | 'gateDispatch'
  | 'preflight'
  | 'preflightHandled'
  | 'gateAssign'
  | 'printable'
  | 'unmapped'
  | 'late'
  | 'gateDeliver'
  | 'progress'
  | 'exception'
  | 'nextBatch'
  | 'stragglerCommitment'
  | 'stragglerMandatory'
  | 'stragglerReminder';

// Literal keys: `t()` reads these by lookup, which the i18n source-coverage
// scan cannot follow, so `__tests__/focusLabels.test.ts` pins them.
export const FOCUS_BADGE_LABEL = {
  gate: 'dispatch.board.focus.badge.gate',
  mustFix: 'dispatch.board.focus.badge.mustFix',
  handled: 'dispatch.board.focus.badge.handled',
  canStart: 'dispatch.board.focus.badge.canStart',
  attention: 'dispatch.board.focus.badge.attention',
  progress: 'dispatch.board.focus.badge.progress',
  exception: 'dispatch.board.focus.badge.exception',
  reminder: 'dispatch.board.focus.badge.reminder',
} as const;
type FocusBadge = keyof typeof FOCUS_BADGE_LABEL;
export type FocusBadgeKey = (typeof FOCUS_BADGE_LABEL)[FocusBadge];

export const FOCUS_KIND_STYLE = {
  gateDispatch: { badge: 'gate', tone: 'live' },
  preflight: { badge: 'mustFix', tone: 'block' },
  preflightHandled: { badge: 'handled', tone: 'ok' },
  gateAssign: { badge: 'gate', tone: 'live' },
  printable: { badge: 'canStart', tone: 'ok' },
  unmapped: { badge: 'attention', tone: 'attn' },
  late: { badge: 'attention', tone: 'attn' },
  gateDeliver: { badge: 'gate', tone: 'live' },
  progress: { badge: 'progress', tone: 'live' },
  exception: { badge: 'exception', tone: 'block' },
  nextBatch: { badge: 'reminder', tone: 'mut' },
  stragglerCommitment: { badge: 'attention', tone: 'attn' },
  stragglerMandatory: { badge: 'mustFix', tone: 'block' },
  // `attn`, not `mut`: the phone reminder card picks only attn / block cards.
  stragglerReminder: { badge: 'reminder', tone: 'attn' },
} as const satisfies Record<FocusKind, { badge: FocusBadge; tone: FocusTone }>;

/** The straggler card kind per wire deadline category (O4); a new category fails to compile. */
export const STRAGGLER_KIND = {
  commitment: 'stragglerCommitment',
  mandatory: 'stragglerMandatory',
  reminder: 'stragglerReminder',
} as const satisfies Record<FlowGateCategory, FocusKind>;

export const FOCUS_TEXT = {
  gateDispatchTitle: 'dispatch.board.focus.gateDispatch.title',
  gateDispatchTitleCutoffOnly: 'dispatch.board.focus.gateDispatch.titleCutoffOnly',
  gateDispatchSubSoon: 'dispatch.board.focus.gateDispatch.subSoon',
  gateDispatchSubLater: 'dispatch.board.focus.gateDispatch.subLater',
  gateDispatchSubPackClosed: 'dispatch.board.focus.gateDispatch.subPackClosed',
  gateDispatchSubPastCutoff: 'dispatch.board.focus.gateDispatch.subPastCutoff',
  gateDispatchSubByCutoff: 'dispatch.board.focus.gateDispatch.subByCutoff',
  preflightTitle: 'dispatch.board.focus.preflight.title',
  preflightTitleNotFound: 'dispatch.board.focus.preflight.titleNotFound',
  preflightTitleMixed: 'dispatch.board.focus.preflight.titleMixed',
  preflightSub: 'dispatch.board.focus.preflight.sub',
  preflightHandledTitle: 'dispatch.board.focus.preflightHandled.title',
  preflightHandledSub: 'dispatch.board.focus.preflightHandled.sub',
  gateAssignTitle: 'dispatch.board.focus.gateAssign.title',
  gateAssignSub: 'dispatch.board.focus.gateAssign.sub',
  printableTitle: 'dispatch.board.focus.printable.title',
  printableSubRemaining: 'dispatch.board.focus.printable.subRemaining',
  printableSubNoneLeft: 'dispatch.board.focus.printable.subNoneLeft',
  unmappedTitle: 'dispatch.board.focus.unmapped.title',
  unmappedSub: 'dispatch.board.focus.unmapped.sub',
  lateTitle: 'dispatch.board.focus.late.title',
  lateTitleMany: 'dispatch.board.focus.late.titleMany',
  lateSub: 'dispatch.board.focus.late.sub',
  gateDeliverTitle: 'dispatch.board.focus.gateDeliver.title',
  gateDeliverSubEta: 'dispatch.board.focus.gateDeliver.subEta',
  gateDeliverSubReadOnly: 'dispatch.board.focus.gateDeliver.subReadOnly',
  progressTitle: 'dispatch.board.focus.progress.title',
  progressSubDrivers: 'dispatch.board.focus.progress.subDrivers',
  progressSubClosed: 'dispatch.board.focus.progress.subClosed',
  exceptionTitle: 'dispatch.board.focus.exception.title',
  exceptionSub: 'dispatch.board.focus.exception.sub',
  nextBatchTitle: 'dispatch.board.focus.nextBatch.title',
  nextBatchSub: 'dispatch.board.focus.nextBatch.sub',
  stragglerTitle: 'dispatch.board.focus.straggler.title',
  stragglerSub: 'dispatch.board.focus.straggler.sub',
} as const;
export type FocusTextKey = (typeof FOCUS_TEXT)[keyof typeof FOCUS_TEXT];

/** A translation key plus its params; a counted number is always `count`. */
export interface FocusText {
  key: FocusTextKey;
  params: Record<string, string | number>;
}

export interface FocusCard {
  kind: FocusKind;
  tone: FocusTone;
  badge: FocusBadgeKey;
  title: FocusText;
  sub: FocusText | null;
  target: BoardTarget;
}

export interface FocusInput {
  /** The real today (merchant calendar date), never the selected date. */
  today: string;
  now: Date;
  todayRow: BoardDay | undefined;
  /** The earliest later date with jobs awaiting dispatch. */
  nextBatch: BoardDay | undefined;
  /** Tenant local gate times (HH:mm); `null` = flow rules not loaded. */
  gates: { packaging: string | null; dispatch: string | null; windowEnd: string | null } | null;
  /** Display name of the flow's outside system, already translated. */
  provider: string | null;
  /** Steps and deadlines of the tenant flow; `null` = flow not loaded, no current step. */
  flowModel: FlowStepModel | null;
  preview?: { anomalies: readonly { reason: 'order_cancelled' | 'order_not_found' }[] };
  exceptionList?: { items: readonly { status: string; reason: string }[] };
  assignment?: { unassigned: { driver_tasks: number }; unmapped_worker: { jobs: number } };
  enRouteDriverCount?: number;
  eta?: {
    late: number;
    latestLateName: string | null;
    latestLateMs: number | null;
    latestMs: number | null;
  };
}

export interface FocusFormat {
  /** "10/1" */
  monthDay(date: string): string;
  /** "10/2 周五" */
  batchDay(date: string): string;
  /** Tenant wall clock HH:mm of a unix ms instant. */
  clock(ms: number): string;
  /** Translated board stage name. */
  stage(stage: BoardStage): string;
}

interface Ctx {
  input: FocusInput;
  fmt: FocusFormat;
  row: BoardDay | undefined;
  phase: Phase;
  straggler: Straggler | null;
}

type Slot = (ctx: Ctx) => FocusCard | null;

const text = (key: FocusTextKey, params: FocusText['params'] = {}): FocusText => ({ key, params });

function card(
  kind: FocusKind,
  target: BoardTarget,
  title: FocusText,
  sub: FocusText | null,
): FocusCard {
  const style = FOCUS_KIND_STYLE[kind];
  return { kind, tone: style.tone, badge: FOCUS_BADGE_LABEL[style.badge], title, sub, target };
}

const todayAt = (ctx: Ctx, stage: BoardStage): BoardTarget => ({ date: ctx.input.today, stage });

/** Preflight anomalies today: the preview's list once it is back, else the board count. */
function anomalyCount(ctx: Ctx): number {
  return ctx.input.preview?.anomalies.length ?? ctx.row?.signals.preflightAnomalies ?? 0;
}

const gateDispatch: Slot = (ctx) => {
  const { gates, now, today } = ctx.input;
  if (!ctx.row || !gates?.packaging || !gates.dispatch) return null;
  const gate = ctx.row.nextGate;
  const minutes =
    gate?.kind === 'packaging_cutoff'
      ? Math.ceil((Date.parse(gate.atIso) - now.getTime()) / 60_000)
      : null;
  const windowed = hasDispatchWindow(gates.packaging, gates.dispatch);
  let sub: FocusText;
  if (!windowed) {
    // No span, and "packaging closed" has no meaning here (#2631).
    sub = dispatchCutoffAhead(gate, gates.dispatch, now.getTime())
      ? text(FOCUS_TEXT.gateDispatchSubByCutoff, { at: gates.dispatch })
      : text(FOCUS_TEXT.gateDispatchSubPastCutoff);
  } else if (minutes !== null && minutes > 0) {
    sub =
      minutes < 60
        ? text(FOCUS_TEXT.gateDispatchSubSoon, { count: minutes })
        : text(FOCUS_TEXT.gateDispatchSubLater, { at: gates.packaging });
  } else if (gate?.kind === 'packaging_cutoff' || gate?.kind === 'dispatch_cutoff') {
    // A packaging gate whose time has come lags the clock until the next board
    // read: the dispatch window is open, never a zero or negative countdown (S2).
    sub = text(FOCUS_TEXT.gateDispatchSubPackClosed, { at: gates.dispatch });
  } else {
    sub = text(FOCUS_TEXT.gateDispatchSubPastCutoff);
  }
  return card(
    'gateDispatch',
    todayAt(ctx, 'awaiting_dispatch'),
    windowed
      ? text(FOCUS_TEXT.gateDispatchTitle, {
          day: ctx.fmt.monthDay(today),
          from: gates.packaging,
          to: gates.dispatch,
        })
      : text(FOCUS_TEXT.gateDispatchTitleCutoffOnly, {
          day: ctx.fmt.monthDay(today),
          at: gates.dispatch,
        }),
    sub,
  );
};

const preflight: Slot = (ctx) => {
  const count = anomalyCount(ctx);
  if (count === 0) return null;
  const anomalies = ctx.input.preview?.anomalies;
  let title: FocusTextKey = FOCUS_TEXT.preflightTitleMixed;
  if (anomalies?.every((a) => a.reason === 'order_cancelled')) title = FOCUS_TEXT.preflightTitle;
  else if (anomalies?.every((a) => a.reason === 'order_not_found')) {
    title = FOCUS_TEXT.preflightTitleNotFound;
  }
  return card(
    'preflight',
    todayAt(ctx, 'awaiting_dispatch'),
    text(title, { count }),
    text(FOCUS_TEXT.preflightSub),
  );
};

const preflightHandled: Slot = (ctx) => {
  if (anomalyCount(ctx) > 0) return null;
  const cancelled =
    ctx.input.exceptionList?.items.filter((item) => item.reason === 'preflight_order_cancelled')
      .length ?? 0;
  if (cancelled === 0) return null;
  return card(
    'preflightHandled',
    todayAt(ctx, 'awaiting_dispatch'),
    text(FOCUS_TEXT.preflightHandledTitle),
    text(FOCUS_TEXT.preflightHandledSub, { count: cancelled }),
  );
};

const gateAssign: Slot = (ctx) => {
  const { assignment, gates, provider } = ctx.input;
  const unassigned = assignment?.unassigned.driver_tasks ?? 0;
  if (unassigned === 0 || !gates?.dispatch || provider === null) return null;
  return card(
    'gateAssign',
    todayAt(ctx, 'awaiting_assignment'),
    text(FOCUS_TEXT.gateAssignTitle, { count: unassigned, provider }),
    text(FOCUS_TEXT.gateAssignSub, { at: gates.dispatch, provider }),
  );
};

const printable: Slot = (ctx) => {
  const { gates } = ctx.input;
  const ready = ctx.row?.counts.awaiting_departure ?? 0;
  if (ready === 0) return null;
  let sub: FocusText | null = null;
  if (gates !== null) {
    if ((ctx.row?.counts.awaiting_assignment ?? 0) > 0)
      sub = text(FOCUS_TEXT.printableSubRemaining);
    else if (gates.dispatch) sub = text(FOCUS_TEXT.printableSubNoneLeft, { at: gates.dispatch });
  }
  return card(
    'printable',
    todayAt(ctx, 'awaiting_departure'),
    text(FOCUS_TEXT.printableTitle, { count: ready }),
    sub,
  );
};

const unmapped: Slot = (ctx) => {
  const { assignment, provider } = ctx.input;
  const stops = assignment?.unmapped_worker.jobs ?? 0;
  if (stops === 0) return null;
  return card(
    'unmapped',
    todayAt(ctx, 'awaiting_assignment'),
    text(FOCUS_TEXT.unmappedTitle, { count: stops }),
    provider === null ? null : text(FOCUS_TEXT.unmappedSub, { provider }),
  );
};

const late: Slot = (ctx) => {
  const { eta, gates, provider } = ctx.input;
  if (!eta || eta.late === 0) return null;
  let title: FocusText;
  if (eta.late === 1 && eta.latestLateName !== null && eta.latestLateMs !== null) {
    title = text(FOCUS_TEXT.lateTitle, {
      name: eta.latestLateName,
      at: ctx.fmt.clock(eta.latestLateMs),
    });
  } else if (gates?.windowEnd) {
    title = text(FOCUS_TEXT.lateTitleMany, { count: eta.late, at: gates.windowEnd });
  } else {
    return null;
  }
  return card(
    'late',
    todayAt(ctx, 'en_route'),
    title,
    provider === null ? null : text(FOCUS_TEXT.lateSub, { provider }),
  );
};

const gateDeliver: Slot = (ctx) => {
  const { eta, gates, provider } = ctx.input;
  if ((eta?.late ?? 0) > 0 || !gates?.windowEnd) return null;
  let sub: FocusText | null = null;
  if (eta !== undefined && eta.latestMs !== null) {
    sub = text(FOCUS_TEXT.gateDeliverSubEta, { at: ctx.fmt.clock(eta.latestMs) });
  } else if (provider !== null) {
    sub = text(FOCUS_TEXT.gateDeliverSubReadOnly, { provider });
  }
  return card(
    'gateDeliver',
    todayAt(ctx, 'en_route'),
    text(FOCUS_TEXT.gateDeliverTitle, { at: gates.windowEnd }),
    sub,
  );
};

const progress: Slot = (ctx) => {
  if (!ctx.row) return null;
  const { enRouteDriverCount } = ctx.input;
  let sub: FocusText | null = null;
  if (ctx.phase === 'closed') sub = text(FOCUS_TEXT.progressSubClosed);
  else if (enRouteDriverCount !== undefined) {
    sub = text(FOCUS_TEXT.progressSubDrivers, { count: enRouteDriverCount });
  }
  return card(
    'progress',
    todayAt(ctx, 'delivered'),
    // The denominator is the live `total` (#2481 B1), not the six-column sum.
    text(FOCUS_TEXT.progressTitle, { delivered: ctx.row.counts.delivered, total: ctx.row.total }),
    sub,
  );
};

// O7: before any job has left, assign / depart have no progress to show.
const progressOnceLeft: Slot = (ctx) =>
  ctx.row && ctx.row.counts.en_route + ctx.row.counts.delivered > 0 ? progress(ctx) : null;

const exception: Slot = (ctx) => {
  const failed =
    ctx.input.exceptionList?.items.filter((item) => item.status === 'exception').length ?? 0;
  if (failed === 0) return null;
  return card(
    'exception',
    todayAt(ctx, 'exception_or_cancelled'),
    text(FOCUS_TEXT.exceptionTitle, { count: failed }),
    text(FOCUS_TEXT.exceptionSub),
  );
};

// The sub-text states only today's count awaiting dispatch: no dispatch time
// (owner decision 2 of #2437), so it needs no flow rule.
const nextBatch: Slot = (ctx) => {
  const batch = ctx.input.nextBatch;
  if (!batch) return null;
  return card(
    'nextBatch',
    { date: batch.date, stage: 'awaiting_dispatch' },
    text(FOCUS_TEXT.nextBatchTitle, {
      day: ctx.fmt.batchDay(batch.date),
      count: batch.counts.awaiting_dispatch,
    }),
    text(FOCUS_TEXT.nextBatchSub, { count: batch.counts.awaiting_dispatch }),
  );
};

// One factory, three kinds: the deciding deadline's wire category picks the
// kind, so its place and style (O4). `earliestStraggler` returns at most one,
// so at most one of the three slots holds.
const straggler =
  (category: FlowGateCategory): Slot =>
  (ctx) => {
    const s = ctx.straggler;
    if (!s || s.deadline.category !== category) return null;
    return card(
      STRAGGLER_KIND[category],
      todayAt(ctx, s.step),
      text(FOCUS_TEXT.stragglerTitle, { count: s.count, step: ctx.fmt.stage(s.step) }),
      text(FOCUS_TEXT.stragglerSub, { at: s.deadline.localTime }),
    );
  };
const stragglerCommitment = straggler('commitment');
const stragglerMandatory = straggler('mandatory');
const stragglerReminder = straggler('reminder');

const preflightOrHandled: Slot = (ctx) => preflight(ctx) ?? preflightHandled(ctx);

/**
 * Slot order per phase; the first four cards that hold are shown. Order
 * (decision 5): commitment (late, then a commitment straggler) > mandatory >
 * the current step's action cards > reminder > progress > exception > next batch.
 * `gateDeliver` yields nothing while a driver is late, so `late` covers it.
 */
const SLOTS = {
  none: [exception, nextBatch],
  dispatch: [late, gateDispatch, preflightOrHandled, exception, nextBatch],
  assign: [
    late,
    stragglerCommitment,
    stragglerMandatory,
    gateAssign,
    printable,
    unmapped,
    stragglerReminder,
    progressOnceLeft,
    exception,
    nextBatch,
  ],
  depart: [
    late,
    stragglerCommitment,
    stragglerMandatory,
    printable,
    stragglerReminder,
    progressOnceLeft,
    exception,
    nextBatch,
  ],
  deliver: [
    late,
    stragglerCommitment,
    stragglerMandatory,
    gateDeliver,
    stragglerReminder,
    progress,
    exception,
    nextBatch,
  ],
  closed: [progress, exception, nextBatch],
} as const satisfies Record<Phase, readonly Slot[]>;

const MAX_FOCUS_CARDS = 4;

/**
 * Whether today's board counts alone show something to act on: any live job
 * today (every column but delivered feeds a slot, and a fully delivered day
 * still has its progress card), an exception or cancelled job, a preflight
 * anomaly, or a later batch to dispatch. When this holds, no card means a
 * slot lacked data or a rule — never "all clear".
 */
export function hasActionableToday(
  todayRow: BoardDay | undefined,
  nextBatch: BoardDay | undefined,
): boolean {
  if (nextBatch !== undefined) return true;
  if (!todayRow) return false;
  return (
    todayRow.total > 0 ||
    todayRow.counts.exception_or_cancelled > 0 ||
    todayRow.signals.preflightAnomalies > 0
  );
}

/** Every card that holds today, in slot order, before the cap. */
export function holdFocusCards(input: FocusInput, fmt: FocusFormat): FocusCard[] {
  const row = input.todayRow;
  const phase: Phase =
    !row || row.total === 0 || input.flowModel === null
      ? 'none'
      : (phaseOf(row, input.flowModel, input.now.getTime()) ?? 'none');
  const straggler =
    input.flowModel && row ? earliestStraggler(input.flowModel, row, input.now.getTime()) : null;
  const ctx: Ctx = { input, fmt, row, phase, straggler };
  return SLOTS[phase].map((slot) => slot(ctx)).filter((c): c is FocusCard => c !== null);
}

/** The cards shown: the first four that hold; a live exception card keeps the last place. */
export function capFocusCards(held: readonly FocusCard[]): FocusCard[] {
  const shown = held.slice(0, MAX_FOCUS_CARDS);
  // Decision 5: the exception card is never squeezed out; it takes the last place.
  const failed = held.find((c) => c.kind === 'exception');
  if (failed && !shown.includes(failed)) shown[MAX_FOCUS_CARDS - 1] = failed;
  return shown;
}

export function deriveFocusCards(input: FocusInput, fmt: FocusFormat): FocusCard[] {
  return capFocusCards(holdFocusCards(input, fmt));
}
